import { Store } from '@ngrx/store';
import { IInfoSelect, IInfoSelectItem } from '../../../store/models/select.model';
import {
  DataSourceParams,
  EFilterType,
  ETableColumnType,
  ITableFilter,
  ITableFIlterGroup,
  ITableFilterItem,
  ITableMassiveAction,
} from '../../../store/models/table.model';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  StaticProvider,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ITableColumn } from 'app/store/models/table.model';
import { BaseDataSource } from 'app/datasources/base.datasource';
import { takeUntil, tap, timeout } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatRadioChange } from '@angular/material/radio';
import { MatSort, Sort } from '@angular/material/sort';
import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { Subject, Subscription } from 'rxjs';
import { openDialog } from 'app/store/actions/page.action';
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { IReducers } from 'app/store/reducers';
import { Button } from 'app/store/models/button.model';
import { faSearch, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { PaginatorComponent } from '../paginator/paginator.component';
import { MatTable } from '@angular/material/table';
import { SelectComponent } from '../select/select.component';
import { SessionService } from '../../../store/services/session.service';
import { IUserSessionData } from '../../../store/models/user.model';
import { TableMobileRowDirective } from '../directives/table-mobile-row.directive';
import { MobileService } from '../../../store/services/mobile.service';

import * as _ from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { PopoverContentComponent } from '../popover-content/popover-content.component';

export const TABLE_COMPONENT = new InjectionToken<TableComponent>(
  'TableComponent',
);

const TABLE_QUERY_PARAMS = ['page', 'pageSize', 'filters'];

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges, OnDestroy {
  @ViewChildren('selectFilters', { read: SelectComponent })
  selectFilters: SelectComponent[];
  @Input() columns: ITableColumn<any>[];
  @Input() dataSource: BaseDataSource<any>;
  @Input() dataSourceParams: DataSourceParams;
  @Input() activeFilters: { [key: string]: any } = {};
  @Input() filters: ITableFilter[] = [];
  @Input() filterGroups: ITableFIlterGroup[] = [];
  @Input() selectable: boolean;
  @Input() massiveActions: ITableMassiveAction<any>[];
  @Input() massiveActionsAsButtons = false;
  @Input() rowClass: (row: any) => any;
  @Input() rowHover: (row: any) => string | undefined;
  @Input() preselected: any[];
  @Input() preselectedIdentifier: string;
  @Input() forceSelectPage = false;
  @Output() data: EventEmitter<any> = new EventEmitter<any>();
  @Input() emptyDataText: string = 'No data matching the filter.';
  @Input() showPagination = true;
  @Input() enableSingleSelection = false;


  @Input() useRouteNavigation: boolean;
  @Output() rowSelected: EventEmitter<SelectionChange<any>> = new EventEmitter<
    SelectionChange<any>
  >();
  @ViewChild('paginatorComponent') paginatorComponent: PaginatorComponent;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild('table', { read: MatTable }) table: MatTable<any>;
  @ViewChild('lazyLoadingBorder') lazyLoadingBorder: ElementRef;

  @ContentChild(TableMobileRowDirective) mobileRows: TableMobileRowDirective;
  dcpProviders: StaticProvider[] = [
    {
      provide: TABLE_COMPONENT,
      useValue: this,
    },
  ];
  selection: SelectionModel<any>;
  iconSearch = faSearch;
  currentPage = 1;
  currentPageSize = 10;
  appliedFilters = {};
  useSession: IUserSessionData;
  isMobileView: boolean;
  mobileData: any[] = [];
  lazyLoadNextPageInProgress: boolean;
  componentReady = false;
  loadingInProgress: boolean;
  itemsCollected: boolean;
  private subscriptions: Subscription[] = [];
  private searchValues: {
    [key: string]: string;
  } = {};
  private destroyed$ = new Subject<void>();
  private doingManualRefresh: boolean;
  private manualSelectionApplied: boolean;
  private dataLoadSubsciption: Subscription;
  private initialLoading: boolean;


  constructor(
    private store: Store<IReducers>,
    private readonly sessionService: SessionService,
    private readonly mobileService: MobileService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    const initialSelection = [];
    const allowMultiSelect = true;
    this.selection = new SelectionModel<any>(
      allowMultiSelect,
      initialSelection,
    );
    this.selection.changed.subscribe(changes => this.rowSelected.emit(changes));
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((session: IUserSessionData) => {
        this.useSession = session;
      });

    this.mobileService.observe().subscribe(isMobile => {
      if (!this.mobileRows) {
        this.isMobileView = false;
        return;
      }
      if (this.isMobileView !== undefined && this.isMobileView !== isMobile) {
        this.mobileData = [];
        this.dataSource.reInit();
        setTimeout(() => this.initPaginator());
      }

      if (!this.isMobileView && isMobile) {
        this.isMobileView = true;
        this.dataSource.reInit();
        this.changeDetectorRef.markForCheck();
        return;
      }
      this.isMobileView = isMobile;
      this.changeDetectorRef.markForCheck();
    });
  }

  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll() {
    if (!this.isMobileView || !this.lazyLoadingBorder) {
      return;
    }

    const lazyBorderRect = this.lazyLoadingBorder.nativeElement.getBoundingClientRect();

    if (!this.lazyLoadNextPageInProgress && Math.round(lazyBorderRect.top) <= window.innerHeight && this.dataSource.hasMoreData) {
      this.lazyLoadNextPageInProgress = true;

      if (this.useRouteNavigation) {
        this.router.navigate(['/'], {
          queryParams: {
            page: this.currentPage + 1,
            pageSize: this.currentPageSize,
          },
        });

        return;
      }

      this.dataSource.loadNextPage();
    }
  }

  isExpansionDetailRow = (i: number, row: any) => {
    const extraColumn = this.getExtraColumnName();
    return row.hasOwnProperty(extraColumn);
  };

  getColumns() {
    return this.columns.filter(c => c.disabled !== true); // .filter(column => !column.isExtra).filter(column => column !== null);
  }

  ngOnChanges() {
    this.initAppliedFilters();

    if (this.dataSourceParams) {
      this.initialLoading = true;
      this.dataSource.load(this.getDataSourceParams());
    }
  }

  selectElement(element: any) {
    console.log('here 1', this.enableSingleSelection, this.selection.selected);
    if(this.enableSingleSelection && this.selection.selected.length > 0) {
      this.selection.clear();
    }
    this.manualSelectionApplied = true;
    this.selection.select(element);
  }

  forceRefresh(): void {
    this.dataLoadSubsciption.unsubscribe();
    this.doingManualRefresh = true;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => this.subscribeForData());
  }

  ngOnInit() {
    this.initAppliedFilters();

    if (this.dataSource) {
      this.dataSource.loading$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(loading => {
          this.initialLoading = true;
          this.loadingInProgress = loading;
        });

      this.dataSource.loading$
        .pipe(timeout(1000))
        .subscribe(
          () => {
          },
          () => {
            if (!this.initialLoading) {
              this.dataSource.load({
                ...this.getDataSourceParams(),
                page: this.currentPage,
                pageSize: this.currentPageSize,
              });
            }
          },
        );
    }
    if (this.dataSourceParams && this.dataSourceParams.pageSize) {
      this.currentPageSize = this.dataSourceParams.pageSize;
    }

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        if (!this.useRouteNavigation) {
          return;
        }

        let foundTableFilter = false;
        for (let key of TABLE_QUERY_PARAMS) {
          if (Object.keys(params).indexOf(key) !== -1) {
            foundTableFilter = true;
          }
        }

        if (!foundTableFilter) {
          return;
        }

        const dataSourceParams = params;
        // @ts-ignore
        let filters = {};

        if (dataSourceParams && dataSourceParams.filters) {
          // @ts-ignore
          filters = JSON.parse(dataSourceParams.filters);

          this.appliedFilters = filters;
        } else {
          filters = {};
        }
        const currentPage = parseInt(dataSourceParams.page || 1, 10);
        const currentPageSize = parseInt(dataSourceParams.pageSize || 10, 10);

        this.currentPage = currentPage;
        this.currentPageSize = currentPageSize;
        if (this.paginatorComponent) {
          this.paginatorComponent.componentReady = false;
          this.paginatorComponent.pageSize = this.currentPageSize;
          this.paginatorComponent.detectChanged();
          setTimeout(() => this.paginatorComponent.componentReady = true);
        }

        this.saveTableState(dataSourceParams);

        if (this.dataSource) {
          this.dataSource.discardCurrent();
          this.dataSource.load({
            ...this.dataSourceParams,
            page: this.currentPage,
            pageSize: this.currentPageSize,
            filters,
          });
        }
      });

    if (this.useRouteNavigation) {
      const tableData = this.getTableData();

      if (tableData) {
        this.router.navigate([], {
          queryParams: {
            ...this.activatedRoute.snapshot.queryParams,
            ...tableData,
          },
        });
        return;
      }
    }
    if (!this.dataSourceParams) {
      this.dataSourceParams = {
        page: 1,
        pageSize: 10,
        sortAttr: 'id',
        sortDir: 'asc',
        filters: {},
      };
    }

    if (this.selectable) {
      this.columns = [...this.getColumns()];
    }

    this.subscribeForData();

  }

  private initAppliedFilters() {
    if (this.activeFilters) {
      this.appliedFilters = {
        ...this.activeFilters,
        ...this.appliedFilters,
      };
    }
  }

  private getDataSourceParams(): DataSourceParams {
    return {
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
    };
  }

  subscribeForData() {
    this.dataLoadSubsciption = this.dataSource.onDataLoaded()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        this.loadingInProgress = false;
        this.lazyLoadNextPageInProgress = false;
        if (this.isMobileView) {
          if (this.doingManualRefresh) {
            this.mobileData = data;
          } else {
            this.mobileData = _.concat(this.mobileData, data);
          }
          this.doingManualRefresh = false;
          this.changeDetectorRef.markForCheck();
          return;
        }

        this.doingManualRefresh = false;
        if (!this.manualSelectionApplied) {
          this.getColumns().forEach(column => {
            if (
              this.preselected &&
              Array.isArray(this.preselected) &&
              data.length
            ) {
              this.preselected.forEach(s => {
                data.forEach(d => {
                  if (
                    this.preselectedIdentifier &&
                    d[this.preselectedIdentifier] &&
                    s[this.preselectedIdentifier] &&
                    d[this.preselectedIdentifier] ===
                    s[this.preselectedIdentifier] &&
                    !this.isSelected(d)
                  ) {
                    this.selection.select(d);
                  } else if (JSON.stringify(d) === JSON.stringify(s)) {
                    this.selection.select(d);
                  }
                });
              });
            }

            if (this.forceSelectPage) {
              data.forEach(d => this.selection.select(d));
            }
          });
        }
        this.data.emit(data);
        setTimeout(() => this.itemsCollected = true);
        this.changeDetectorRef.markForCheck();
      });
  }

  initPaginator() {
    if (!this.paginatorComponent) {
      return;
    }
    this.subscriptions.push(
      this.paginatorComponent.change
        .pipe(
          tap(paginator => {
            // this.manualSelectionApplied = true;

            this.currentPage = paginator.page;
            this.currentPageSize = paginator.pageSize;

            if (this.useRouteNavigation) {
              this.router.navigate([], {
                queryParams: {
                  ...this.activatedRoute.snapshot.queryParams,
                  page: this.currentPage,
                  pageSize: this.currentPageSize,
                },
              });
            } else {
              this.dataSource.load({
                ...this.dataSourceParams,
                page: paginator.page,
                pageSize: paginator.pageSize,
                filters: {
                  ...this.dataSourceParams.filters,
                  ...this.appliedFilters,
                },
              });
            }
            this.dataSource.clearInternal();
            window.scroll(0, 0);
          }),
        )
        .subscribe(),
    );
  }

  ngAfterViewInit() {
    if (this.isMobileView && !this.mobileRows) {
      setTimeout(() => this.isMobileView = false);
    }
    this.initPaginator();

    if (this.sort) {
      this.subscriptions.push(
        this.sort.sortChange.subscribe((sort: Sort) => {
          this.dataSourceParams.sortAttr = sort.active;
          this.dataSourceParams.sortDir = sort.direction;
          this.dataSource.load({
            ...this.dataSourceParams,
            filters: {
              ...this.dataSourceParams.filters,
              ...this.appliedFilters,
            },
          });
        }),
      );
    }
  }

  refresh() {
    this.doingManualRefresh = true;
    this.dataSource.load({
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
      page: this.currentPage,
      pageSize: this.currentPageSize,
    });
  }

  getColumnRoute(column: ITableColumn<any>, element) {
    const buttonProperties = this.getButtonProperties(column, element);
    if (buttonProperties && buttonProperties.route) {
      return buttonProperties.route;
    }

    const currentElementId = this.dataSource.data.indexOf(element);
    if (column.route || buttonProperties.route) {
      return (!column.type ||
        column.type === ETableColumnType.TEXT ||
        column.type === ETableColumnType.BUTTON) &&
      typeof column.route === 'string'
        ? column.route
        : column.route(
          element,
          this.dataSource.data[currentElementId - 1] || null,
          this.dataSource.data[currentElementId + 1] || null,
        );
    }
  }

  hasColumnRoute(column: ITableColumn<any>): boolean {
    if (column.dynamicRoute) {
      return true;
    }

    return column.route && typeof column.route === 'string';
  }

  hasColumnClickEvent(column: ITableColumn<any>): boolean {
    return column.route && typeof column.route === 'function';
  }

  onColumnClick(column: ITableColumn<any>, element: any) {
    if (this.hasColumnClickEvent(column)) {
      const currentElementId = this.dataSource.data.indexOf(element);
      column.route(
        element,
        this.dataSource.data[currentElementId - 1] || null,
        this.dataSource.data[currentElementId + 1] || null,
      );
    }
  }

  getDisplayedColumns(withExtra: boolean = false): string[] {
    if (this.selectable) {
      return ['select', ...this.getColumns().map(column => column.id)];
    }
    return this.getColumns().map(column => {
      // if (!withExtra && !column.isExtra) {
      return column.id;
      // }
    });
  }

  hasExtraColumn(): boolean {
    return this.getColumns().filter(column => column.isExtra).length === 1;
  }

  getExtraColumnName(): string {
    const extraColumn = this.getColumns()
      .filter(column => column.isExtra)
      .filter(column => column !== null);
    if (extraColumn.length === 0) {
      return null;
    }

    return extraColumn[0].id;
  }

  getRowPopover(row): string | PopoverContentComponent {
    if (this.rowHover) {
      return this.rowHover(row);
    }
    return null;
  }

  hasRowPopover(row): boolean {
    if (this.rowHover) {
      return true;
    }

    return false;
  }

  getRowClass(row) {
    if (this.rowClass) {
      const classes = this.rowClass(row);
      const result = '';
      if (Array.isArray(classes)) {
        return result + classes.join(' ');
      } else if (typeof classes === 'string') {
        return result + classes;
      }
    }

    return null;
  }

  getColumnClass(column: ITableColumn<any>, element) {
    if (column.class) {
      if (typeof column.class === 'string') {
        return column.class;
      }

      return column.class(element);
    }

    return null;
  }

  getDefaultSelected(filter: ITableFilter) {
    return filter.emptyValue ? 0 : null;
  }

  appliedFiltersOnEnter(filter: ITableFilter, event: KeyboardEvent, filterId?: string) {
    if (event && event.key === 'Enter') {
      this.applyFilter(filter, event.target.value, filterId);
    }
  }

  applyFilter(filter: ITableFilter, change: any, filterId?, reset?: boolean) {
    if (this.loadingInProgress) {
      return;
    }
    this.itemsCollected = false;
    let targetItems: ITableFilterItem[];
    let value = null;
    switch (filter.type) {
      case EFilterType.SELECT:
        targetItems = filter.items
          .filter(item => item.value === change)
          .filter(item => item !== null);

        if (targetItems && targetItems.length === 1) {
          if (change === 'reset') {
            delete this.appliedFilters[targetItems[0].id];
          } else {
            this.appliedFilters[targetItems[0].id] = change;
          }
          
          if (targetItems[0].resetOtherFilters) {
            filter.items.forEach(f => {
              if (f.id !== targetItems[0].id) {
                delete this.appliedFilters[f.id];
              }
            });
          }
        }

        value = change;

        break;
      case EFilterType.MULTISELECT:
      case EFilterType.TAGS:
        if (reset) {
          filter.items.forEach(f => this.appliedFilters[f.id] = null);
          break;
        }
        targetItems = filter.items
          .filter(item => change.indexOf(item.value) !== -1)
          .filter(item => item !== null);

        if (targetItems && targetItems.length === 1) {
          targetItems.forEach(item => (this.appliedFilters[item.id] = change));
        }

        this.appliedFilters[filter.id] = change;

        value = change;

        break;
      case EFilterType.CHECKBOX:
        targetItems = filter.items
          .filter(
            item => item.id === (change as MatCheckboxChange).source.value,
          )
          .filter(item => item !== null);

        if (targetItems && targetItems.length === 1) {
          this.appliedFilters[targetItems[0].id] = (
            change as MatCheckboxChange
          ).source.value;
        }

        let currentItems =
          this.appliedFilters[
            (change as MatCheckboxChange).source._elementRef.nativeElement
              .dataset.id
            ];
        if (!currentItems) {
          currentItems = [];
        } else {
          currentItems = JSON.parse(currentItems);
        }

        if ((change as MatCheckboxChange).checked === true) {
          if (
            currentItems.indexOf((change as MatCheckboxChange).source.value) ===
            -1
          ) {
            currentItems.push((change as MatCheckboxChange).source.value);
          }
        } else {
          currentItems.splice(
            currentItems.indexOf((change as MatCheckboxChange).source.value),
            1,
          );
        }

        this.appliedFilters[
          (
            change as MatCheckboxChange
          ).source._elementRef.nativeElement.dataset.id
          ] = JSON.stringify(currentItems);

        value = (change as MatCheckboxChange).source.value;

        break;
      case EFilterType.RADIO:
        this.appliedFilters[
          (change as MatRadioChange).source._elementRef.nativeElement.dataset.id
          ] = (change as MatRadioChange).value;
        value = (change as MatRadioChange).value;
        break;
      case EFilterType.DATE:
        this.appliedFilters[filter.id] = change.format('YYYY-MM-DD');
        value = change;
        break;
      case EFilterType.TEXT:
      case EFilterType.SEARCH:
        console.log('here 2', filterId, change);
        this.appliedFilters[filterId] = change;
        value = change;
        break;
    }

    if (filter.onFilterChange) {
      filter.onFilterChange(value);

      if (filter.applyFilter !== undefined && !filter.applyFilter) {
        return;
      }
    }

    if (this.paginatorComponent) {
      this.paginatorComponent.currentPage = 1;
    }

    if (this.isMobileView) {
      this.doingManualRefresh = true;
    }

    const filters = JSON.stringify({
      ...this.dataSourceParams.filters,
      ...this.appliedFilters,
    });
    this.selection.clear();

    if (this.useRouteNavigation) {
      this.router.navigate([], {
        queryParams: {
          page: 1,
          pageSize: this.currentPageSize,
          filters,
        },
      });

      return;
    }

    this.loadingInProgress = true;

    this.dataSource.load({
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
      page: 1, // always reset page to 1
      pageSize: this.currentPageSize,
    });
  }

  applyFilter2(filter, value) {
    if (filter.onFilterChange) {
      filter.onFilterChange(value);
    }

    this.dataSource.load({
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
      page: this.currentPage,
      pageSize: this.currentPageSize,
    });
    this.selection.clear();
  }

  isAllSelected() {
    let itemsCount = 0;
    for (const item of this.dataSource.data) {
      if (this.isSelected(item)) {
        itemsCount++;
      }
    }

    return itemsCount === this.dataSource.data.length;
  }

  masterToggle() {
    this.manualSelectionApplied = true;
    if (this.isAllSelected()) {
      this.dataSource.data.forEach(item => {
        if (this.isSelected(item)) {
          this.delesect(item);
        }
      });
      return;
    }

    this.dataSource.data.forEach(row => {
      if (!this.isSelected(row)) {
        this.selection.select(row);
      }
    });
  }

  onMassiveAction(event) {
    if (event.handler) {
      event.handler(this.selection.selected);
    }
  }

  hasAvailableMassiveActions(): boolean {
    return this.selection.selected.length > 0 &&
      this.massiveActions &&
      this.massiveActions.length > 0;
  }

  isSelected(row: { [x: string]: any; }) {
    if (this.preselectedIdentifier) {
      return (
        this.selection.selected.filter(
          item =>
            item[this.preselectedIdentifier] === row[this.preselectedIdentifier],
        ).length > 0
      );
    }
    return (
      this.selection.selected.filter(
        item => JSON.stringify(item) === JSON.stringify(row),
      ).length > 0
    );
  }

  ngOnDestroy() {
    if (this.subscriptions.length > 0) {
      for (const subscription of this.subscriptions) {
        subscription.unsubscribe();
      }
    }

    this.destroyed$.next();
  }

  onButtonColumnClick(column: ITableColumn<any>, element) {
    const buttonProperties = this.getButtonProperties(column, element);
    if (buttonProperties.onClick) {
      buttonProperties.onClick(element);
    }
  }

  onSelectColumnClick(column: ITableColumn<any>, value, element) {
    if (column.selectProperties && column.selectProperties.onSelection) {
      column.selectProperties.onSelection(value, this, element);
    }

    if( column.getSelectProperties && column.getSelectProperties(element).onSelection) {
      column.getSelectProperties(element).onSelection(value, this, element);
    }
  }

  getSelectItems(column: ITableColumn<any>, element) {
    if (column.selectProperties && column.selectProperties.items) {
      return column.selectProperties.items;
    }

    if (column.selectProperties && column.selectProperties.buildItems) {
      return column.selectProperties.buildItems(element);
    }

    if( column.getSelectProperties && column.getSelectProperties(element).buildItems) {
      return column.getSelectProperties(element).buildItems(element);
    }
  }

  onTriggerColumnChange(column: ITableColumn<any>, element, state: boolean) {
    if (column.triggerProperties && column.triggerProperties.onTrigger) {
      column.triggerProperties.onTrigger(state, element);
    }
  }

  onCheckboxChange(column: ITableColumn<any>, element, state: boolean) {
    if (
      column.type === ETableColumnType.CHECKBOX &&
      column.checkboxProperties.Change
    ) {
      column.checkboxProperties.Change(element, state);
    }
  }

  onCheckboxClick(column: ITableColumn<any>, element) {
    if (
      column.type === ETableColumnType.CHECKBOX &&
      column.checkboxProperties.Click
    ) {
      column.checkboxProperties.Click(element);
    }
  }

  mapComponentData(column: ITableColumn<any>, element) {
    const result = {};
    if (
      column.componentProperties &&
      Object.keys(column.componentProperties.mapData).length > 0
    ) {
      for (const item in column.componentProperties.mapData) {
        if (column.componentProperties.mapData[item]) {
          if (
            column.componentProperties.mapData[item].instance === 'static' &&
            column.componentProperties.mapData[item].target
          ) {
            result[item] = column.componentProperties.mapData[item].target;
          } else if (
            column.componentProperties.mapData[item].instance === 'column'
          ) {
            if (column.componentProperties.mapData[item].target) {
              result[item] =
                element[column.componentProperties.mapData[item].target];
            } else {
              result[item] = element;
            }
          } else if (
            column.componentProperties.mapData[item].instance === 'function'
          ) {
            result[item] =
              column.componentProperties.mapData[item].target(element);
          }
        }
      }

      return result;
    }
  }

  getInfoSelectProperties(
    column: ITableColumn<any>,
    selectProperties: IInfoSelect,
    element: any,
  ): IInfoSelect {
    if (
      column.type === ETableColumnType.SELECT &&
      selectProperties.type === 'info-select'
    ) {
      return {
        ...selectProperties,
        items: selectProperties.buildItems(element) as IInfoSelectItem[],
      };
    }
  }

  openHintDialog(hint: string): void {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: hint,
          },
        },
      }),
    );
  }

  isButtonDisabled(column: ITableColumn<any>, element: any) {
    const buttonProperties = this.getButtonProperties(column, element);
    if (
      column.type === ETableColumnType.BUTTON &&
      buttonProperties &&
      buttonProperties.buttonDisabled
    ) {
      return buttonProperties.buttonDisabled(element);
    }

    return false;
  }

  getButtonProperties(column: ITableColumn<any>, element: any): Button {
    if (column.getButtonProperties) {
      return column.getButtonProperties(element);
    }

    return column.buttonProperties;
  }

  getSelectProperties(column: ITableColumn<any>, element: any) {
    if (column.getSelectProperties) {
      return column.getSelectProperties(element);
    }

    return column.selectProperties;
  }

  getMassiveActionItems() {
    return this.massiveActions.map(i => ({ label: i.label, value: i }));
  }

  getDefaultSelectValue(column: any, element: any) {
    if (!column.selectProperties && !column.getSelectProperties) {
      return element[column.id];
    }

    let selectProperties = column.selectProperties;


    if(column.getSelectProperties) {
      selectProperties = column.getSelectProperties(element);
    }
    if (
      selectProperties.defaultValue === undefined &&
      selectProperties.buildItems
    ) {
      if (selectProperties.asyncBuildItems) {
        return element[column.id];
      }
      const items = selectProperties.buildItems(element);
      for (const item of items) {
        if (item.selected) {
          return item.value;
        }
      }
    }

    return selectProperties.defaultValue !== undefined
      ? selectProperties.defaultValue
      : element[column.id];
  }

  storeSearchFieldValue($event: string, filter: ITableFilter, id: string) {
    this.searchValues[id] = $event;
  }

  applySearchFilter(filter: ITableFilter, id: string) {
    return undefined;
  }

  applyFilterColumn($event: Event, column: ITableColumn<any>) {
    this.appliedFilters[column.headerFilter] = $event.target.value;
    this.selection.clear();

    if (this.useRouteNavigation) {
      const filters = JSON.stringify({
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      });
      this.router.navigate([], {
        queryParams: {
          page: 1,
          pageSize: this.currentPageSize,
          filters,
        },
      });

      return;
    }

    this.dataSource.load({
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
      page: this.currentPage,
      pageSize: this.currentPageSize,
    });
  }

  shouldShowItemsPerPage() {
    return !(this.getColumns().filter(c => c.headerAsPagination).length > 0);
  }

  applyPagination($event: Event) {
    if (!this.paginatorComponent) {
      return;
    }

    this.paginatorComponent.onPageSizeSelection($event.target.value);
  }

  getPaginationOptions(): number[] {
    if (!this.paginatorComponent) {
      return [];
    }

    return this.paginatorComponent.getPaginationOptions();
  }

  delesect(element) {
    if (this.preselectedIdentifier) {
      this.selection.selected.forEach((item, index) => {
        if (
          item[this.preselectedIdentifier] ===
          element[this.preselectedIdentifier]
        ) {
          this.selection.deselect(item);
        }
      });

      return;
    }
    this.selection.deselect(element);
  }

  getTrackBy(column: ITableColumn<any>, element: any) {
    if (column.selectProperties && column.selectProperties.trackBy) {
      return element[column.selectProperties.trackBy];
    }

    if (column.getSelectProperties && column.getSelectProperties(element).trackBy) {
      return element[column.getSelectProperties(element).trackBy];
    }

    return null;
  }

  resetFilter(filter: ITableFilter, filterId: string) {
    if (filter.type === EFilterType.SELECT) {
      delete this.appliedFilters[filter.items[0].id];
    } else if(filter.type === EFilterType.DATE) {
      delete this.appliedFilters[filter.id];
    }

    if (filterId) {
      if (this.selectFilters && this.selectFilters.length) {
        this.selectFilters.forEach(f => {
          if (f.id === filterId) {
            f.resetSelected();
          }
        });
      }
    }

    this.paginatorComponent.currentPage = 1;
    this.selection.clear();

    if (this.useRouteNavigation) {
      const filters = JSON.stringify({
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      });

      this.router.navigate([], {
        queryParams: {
          page: 1,
          pageSize: this.currentPageSize,
          filters,
        },
      });

      return;
    }

    this.dataSource.load({
      ...this.dataSourceParams,
      filters: {
        ...this.dataSourceParams.filters,
        ...this.appliedFilters,
      },
      page: 1, // always reset page to 1
      pageSize: this.currentPageSize,
    });
  }

  getDateFormat(column: any) {
    if (column.dateFormat) {
      return column.dateFormat;
    }
    return 'MM/DD/YYYY hh:mm a';
  }

  onAnchorClick(column: ITableColumn<any>, element: any) {
    if (!column.type || column.type !== ETableColumnType.ANCHOR || !column.anchorHandler) {
      return;
    }

    column.anchorHandler(column, element);
  }

  getSearchFilters(filters: ITableFilter[]): ITableFilter[] {
    return filters.filter(f => f.type === EFilterType.SEARCH);
  }

  getNotSearchFilters(filters: ITableFilter[]) {
    return filters.filter(f => f.type !== EFilterType.SEARCH);
  }

  getSelectedTags(items: ITableFilterItem[]) {
    return items.filter(item => item.selected);
  }


  hasIconPrefix(column: ITableColumn<any>, element: any): boolean {
    if (!column.getFaIconPrefix) {
      return false;
    }
    return column.getFaIconPrefix(element) !== undefined && column.getFaIconPrefix(element) !== null
  }

  getIconPrefix(column: ITableColumn<any>, element: any): IconDefinition {
    return column.getFaIconPrefix(element);
  }

  private getTableData() {
    const tableID = `${window.location.origin}${window.location.pathname}`;
    const data = localStorage.getItem(tableID);

    return data ? JSON.parse(data) : undefined;
  }

  private saveTableState(data: any) {
    const tableID = `${window.location.origin}${window.location.pathname}`;

    localStorage.setItem(tableID, JSON.stringify(data));
  }
}
